import React, { useState } from 'react'; //, { useMemo }
import { css } from '@emotion/core';
import { useParams } from 'react-router-dom';
import { BaseUrl } from '../core/api/endpoints';
import { pathCombine } from '../core/api/module';
import { useMount } from '../core/hooks/useMount';
import LocalDiv from '../localization/LocalDiv';
import Msg from '../localization/Msg';
import { Str } from '../localization/translationKeys';
import { Loader } from '../loading/Loader';
import { useBrowser } from '../language/useBrowser';

const styles = {
  iframe: css`
    width: 100%;
    height: calc(100vh - 51px);
  `,
  notFoundMsg: css`
    margin-top: 60px;
    display: block;
    text-align: center;
    font-weight: bold;
  `,
  loader: css`
    width: 150px;
    height: 150px;
    margin: 60px auto 0;
  `,
};

type Props = {
  folder: string;
  className?: string;
};

/**
 * FileData: a component that renders file data from the server.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const FileData: React.FC<Props> = props => {
  // get file name from URL
  const { fileName } = useParams() as { 
    fileName: string;
  }
  // assign file type based on file extension
  const fileType = () => {
    const file = fileName.toLowerCase().trim();
    if (file.endsWith('.pdf')) {
      return 'application/pdf';
    }
    if (file.endsWith('.docx')) {
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    }
    if (file.endsWith('.pptx')) {
      return 'application/vnd.ms-powerpoint';
    }
    if (file.endsWith('.xlsx')) {
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }
    if (file.endsWith('.htm') || file.endsWith('.html')) {
      return 'text/html';
    }
    return 'application/octet-stream';   
  };

  // get file data from api
  const [url, setUrl] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  const { onIe } = useBrowser();

  const getFileData = () => {
    const apiUrl = pathCombine(BaseUrl, 'document', 'fileData');
    const requestData = { folder: props.folder, fileName: fileName }
    const http = new XMLHttpRequest()
    http.open('POST', apiUrl)
    http.setRequestHeader('content-type', 'application/json')
    http.send(JSON.stringify(requestData))
    http.responseType = 'json';
    http.onload = function () {
      if (http.status === 200) {
        const response = http.response;
        const decoded = window.atob(response);
        const len = decoded.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = decoded.charCodeAt(i);
        }
        if (bytes !== undefined && bytes.length > 0) {
          const blob = new Blob([bytes], { type: fileType() });
//TODO - maybe try something else to hack this into submission for IE?
/* 
          if (true) {
            let iFrameDoc = iframe.contentWindow && iframe.contentWindow.document;
            if (!iFrameDoc) {
            console.log("iFrame security.");
            return;
            }
            iFrameDoc.write(blob);
            iFrameDoc.close();
          }
*/
          setUrl(URL.createObjectURL(blob));
        } else {
          setError(true);
        }
      } else {
        setError(true);
      }
    };
  };

  useMount(() => { !onIe && getFileData(); });

  return (
    onIe
    ? <LocalDiv>
        <span css={styles.notFoundMsg}>Not supported by Internet Explorer - please try another browser</span>
      </LocalDiv>    
    : error 
      ? <LocalDiv>
          <Msg str={Str.NotAvailable} css={styles.notFoundMsg} />
        </LocalDiv>
      : url.length > 0
        ? <iframe
            frameBorder={0}
            css={styles.iframe}
            src={url}
            title={'file-data'}
          />
        : <Loader css={styles.loader} />
  );
};

export default FileData;
